/**
 * SpatialHeatmapMap — Cesium-backed visualization of the analytics
 * spatial heatmap. Each grid cell is rendered as a rectangle entity
 * colored by normalized intensity. The camera auto-fits the bounds of
 * the returned cells on first load, and the base imagery is user-selectable
 * between Google Hybrid / Satellite / Roadmap, OpenStreetMap, and
 * Cesium World Imagery.
 *
 * Kept intentionally small and self-contained so it can sit in the
 * Pattern Analysis tab or on the Dashboard — it doesn't share state
 * with the main CesiumMap / mapStore.
 */
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { Viewer, Entity, EllipseGraphics } from 'resium';
import type { CesiumComponentRef } from 'resium';
import {
  Cartesian3,
  Color,
  Rectangle,
  Viewer as CesiumViewer,
  SceneMode,
  ImageMaterialProperty,
  ConstantProperty,
  HeightReference,
} from 'cesium';
import { useQuery } from '@tanstack/react-query';
import type { HeatmapResponse } from '../../services/analyticsService';
import { sensorsApi } from '../../services/api';
import {
  applyBasemapToViewer,
  BasemapPicker,
} from '../map/basemaps';
import { useMapStore } from '../../store/mapStore';

interface SpatialHeatmapMapProps {
  heatmap: HeatmapResponse;
  /** Height of the map container in px. Default 380. */
  height?: number;
}

/**
 * Map normalized intensity [0..1] to a yellow → amber → red color. The
 * tinting layers over the radial-gradient image (see RADIAL_GRADIENT_PNG)
 * so each cell renders as a soft feathered disc instead of a hard block.
 */
function intensityToColor(intensity: number, count: number): Color {
  // Slightly higher floor on alpha so even low-intensity cells are visible
  // over satellite imagery. The radial gradient handles falloff so we
  // don't need a separate per-cell opacity ramp.
  const alpha = Math.min(0.95, 0.55 + intensity * 0.4);
  if (count === 0) return Color.TRANSPARENT;
  if (intensity < 0.33) return Color.fromCssColorString('#ffd166').withAlpha(alpha);
  if (intensity < 0.66) return Color.fromCssColorString('#ffb547').withAlpha(alpha);
  return Color.fromCssColorString('#e63b4d').withAlpha(alpha);
}

/**
 * Pre-rendered radial-gradient PNG used as the material for each heatmap
 * cell. White center → transparent edge, alpha-only. Tinted per-cell via
 * the entity's color. Generated once at module load; the canvas is GC'd
 * immediately after we read out the data URI.
 *
 * Phase 8: replaces the hard-edged RectangleGraphics rendering. Adjacent
 * cells overlap (we sample at 1.6× cell width below) and their alphas
 * compose to a continuous "heat" gradient rather than discrete blocks.
 */
const RADIAL_GRADIENT_PNG: string = (() => {
  if (typeof document === 'undefined') return ''; // SSR safety
  const size = 128;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  if (!ctx) return '';
  const cx = size / 2;
  const cy = size / 2;
  const gradient = ctx.createRadialGradient(cx, cy, 0, cx, cy, cx);
  // Center solid, edge transparent. The intermediate stops shape the
  // falloff — 0.6 alpha at half-radius keeps the disc visible without
  // a hard inner ring.
  gradient.addColorStop(0.0, 'rgba(255, 255, 255, 1.0)');
  gradient.addColorStop(0.4, 'rgba(255, 255, 255, 0.85)');
  gradient.addColorStop(0.7, 'rgba(255, 255, 255, 0.45)');
  gradient.addColorStop(1.0, 'rgba(255, 255, 255, 0.0)');
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, size, size);
  return canvas.toDataURL('image/png');
})();

export default function SpatialHeatmapMap({
  heatmap,
  height = 380,
}: SpatialHeatmapMapProps) {
  const resiumRef = useRef<CesiumComponentRef<CesiumViewer> | null>(null);
  // Basemap is app-wide — pull from the shared mapStore so choosing a basemap
  // on the live map also updates the heatmap (and vice versa).
  const basemapId = useMapStore((s) => s.basemapId);
  const setBasemapId = useMapStore((s) => s.setBasemapId);

  // ── Sensor coverage fallback ────────────────────────────────────────────
  // When the heatmap has zero activity (common on a quiet day or before
  // detections start landing), framing the server's wide query rectangle
  // shows the operator a near-global view. Instead, pull the tenant's
  // deployed sensors and frame around them — at least the operator sees
  // *their site* with an empty heatmap overlay, which is the contextual
  // baseline the dashboard is supposed to communicate.
  //
  // Cached for 5 min; sensors don't move often.
  const { data: sensorsList } = useQuery({
    queryKey: ['heatmap-sensor-fallback'],
    queryFn: () => sensorsApi.list({ pageSize: 100 }),
    staleTime: 5 * 60 * 1000,
  });

  const sensorBounds = useMemo<Rectangle | null>(() => {
    const sensors = (sensorsList?.data ?? []) as Array<{
      latitude?: number | null;
      longitude?: number | null;
    }>;
    const positioned = sensors.filter(
      (s) =>
        typeof s.latitude === 'number' &&
        typeof s.longitude === 'number' &&
        !(s.latitude === 0 && s.longitude === 0),
    );
    if (positioned.length === 0) return null;
    let minLat = Infinity, maxLat = -Infinity;
    let minLng = Infinity, maxLng = -Infinity;
    for (const s of positioned) {
      const lat = s.latitude as number;
      const lng = s.longitude as number;
      if (lat < minLat) minLat = lat;
      if (lat > maxLat) maxLat = lat;
      if (lng < minLng) minLng = lng;
      if (lng > maxLng) maxLng = lng;
    }
    // Pad ~5 km so a single-sensor deployment doesn't render as a degenerate
    // point. 0.05° ≈ 5.5 km at most latitudes — enough to show sensor
    // surroundings without zooming to the building.
    const pad = 0.05;
    return Rectangle.fromDegrees(
      minLng - pad,
      minLat - pad,
      maxLng + pad,
      maxLat + pad,
    );
  }, [sensorsList]);

  // ── Compute tight camera bounds, in priority order ──────────────────────
  //   1. Bounding box of cells with count > 0  (real activity)
  //   2. Bounding box of deployed sensors      (sensor-coverage fallback)
  //   3. Server-provided wide query rectangle  (last-ditch fallback)
  //
  // The previous version skipped step 2, so empty heatmaps fell straight
  // through to the wide rectangle and the operator saw a near-global view
  // with nothing on it.
  const bounds = useMemo(() => {
    const active = heatmap.cells.filter((c) => c.count > 0);
    if (active.length === 0) {
      if (sensorBounds) return sensorBounds;
      return Rectangle.fromDegrees(
        heatmap.min_lng,
        heatmap.min_lat,
        heatmap.max_lng,
        heatmap.max_lat,
      );
    }
    let minLat = Infinity, maxLat = -Infinity;
    let minLng = Infinity, maxLng = -Infinity;
    for (const c of active) {
      if (c.lat < minLat) minLat = c.lat;
      if (c.lat > maxLat) maxLat = c.lat;
      if (c.lng < minLng) minLng = c.lng;
      if (c.lng > maxLng) maxLng = c.lng;
    }
    // Pad by ~1 cell so edge activity isn't flush against the viewport.
    // Smaller pad = tighter zoom on the actual activity cluster.
    const pad = Math.max(heatmap.resolution * 1.2, 0.005);
    return Rectangle.fromDegrees(
      minLng - pad,
      minLat - pad,
      maxLng + pad,
      maxLat + pad,
    );
  }, [heatmap, sensorBounds]);

  /**
   * Fit the camera to the computed activity bounds. Exposed via a button
   * so users can re-center after panning around, not just on mount.
   */
  const fitToActivity = useCallback(() => {
    const v = resiumRef.current?.cesiumElement;
    if (!v || v.isDestroyed()) return;
    v.camera.flyTo({ destination: bounds, duration: 0.6 });
  }, [bounds]);

  // ── Swap base imagery whenever the user picks a different basemap ──────
  useEffect(() => {
    const v = resiumRef.current?.cesiumElement;
    if (!v || v.isDestroyed()) return;
    void applyBasemapToViewer(v, basemapId);
  }, [basemapId]);

  // ── Auto-fit to activity bounds on first render + whenever the
  // underlying data changes.
  //
  // Cesium has a habit of homing the camera to the whole-earth view on
  // first frame, which would clobber a setView we issue during the
  // initial useEffect. Previous attempts at fixing this used chained
  // setTimeouts (0 / 150 / 450 ms) but were still racy — on a slow
  // first paint Cesium's home would land between our setView calls.
  //
  // The reliable pattern: register a one-shot postRender listener that
  // sets the view AFTER Cesium has actually rendered a frame, then
  // unhooks itself. Combined with an immediate setView so the very
  // first paint is already on the right rectangle.
  useEffect(() => {
    const v = resiumRef.current?.cesiumElement;
    if (!v || v.isDestroyed()) return;

    // First paint — best-effort, before Cesium's home logic runs.
    v.camera.setView({ destination: bounds });

    // Authoritative fit — runs after the next render frame, then
    // disconnects. If Cesium reset the camera between our setView
    // and the first frame, this corrects it.
    let didFit = false;
    const onPostRender = () => {
      if (didFit || v.isDestroyed()) return;
      v.camera.setView({ destination: bounds });
      didFit = true;
      v.scene.postRender.removeEventListener(onPostRender);
      // Switch to render-on-demand for perf now that the camera is
      // settled. Pre-fit we kept it on so postRender fires reliably.
      v.scene.requestRenderMode = true;
      v.scene.requestRender();
    };
    v.scene.postRender.addEventListener(onPostRender);

    return () => {
      if (!v.isDestroyed()) {
        v.scene.postRender.removeEventListener(onPostRender);
      }
    };
  }, [bounds]);

  // Only render cells that actually have activity — empty cells clutter
  // the map and obscure hot spots.
  const cells = useMemo(
    () => heatmap.cells.filter((c) => c.count > 0),
    [heatmap],
  );

  return (
    <div
      className="rounded-lg overflow-hidden border border-navy-700 relative"
      style={{ height }}
    >
      <Viewer
        ref={resiumRef}
        full={false}
        style={{ width: '100%', height: '100%' }}
        sceneMode={SceneMode.SCENE2D}
        animation={false}
        timeline={false}
        baseLayerPicker={false}
        fullscreenButton={false}
        geocoder={false}
        homeButton={false}
        infoBox={false}
        navigationHelpButton={false}
        sceneModePicker={false}
        selectionIndicator={false}
        shouldAnimate={false}
      >
        {cells.map((c) => {
          // Each cell renders as an ellipse tinted by the intensity color
          // and textured with the radial-gradient PNG. We oversize the
          // ellipse (1.6× cell width) so adjacent cells visually merge —
          // that overlap is what produces the tapered, blob-like look
          // instead of the previous grid of hard rectangles.
          //
          // Resolution is in degrees; convert to meters via the standard
          // 111_320 m/degree (good to ~3 decimal places at the latitudes
          // we care about — high precision isn't needed here).
          const radiusMeters = heatmap.resolution * 111_320 * 0.8;
          const fill = intensityToColor(c.intensity, c.count);
          return (
            <Entity
              key={`${c.lat}-${c.lng}`}
              position={Cartesian3.fromDegrees(c.lng, c.lat)}
              name={`${c.count} detection${c.count === 1 ? '' : 's'}`}
              description={`${c.lat.toFixed(4)}, ${c.lng.toFixed(4)} — ${c.count} detection${c.count === 1 ? '' : 's'}`}
            >
              <EllipseGraphics
                semiMajorAxis={new ConstantProperty(radiusMeters)}
                semiMinorAxis={new ConstantProperty(radiusMeters)}
                material={
                  new ImageMaterialProperty({
                    image: new ConstantProperty(RADIAL_GRADIENT_PNG),
                    color: new ConstantProperty(fill),
                    transparent: new ConstantProperty(true),
                  })
                }
                heightReference={new ConstantProperty(HeightReference.CLAMP_TO_GROUND)}
                // No outline — the gradient PNG provides the falloff;
                // any outline would re-introduce the "block" feel.
                outline={new ConstantProperty(false)}
              />
            </Entity>
          );
        })}
      </Viewer>

      {/* Top-left controls — basemap picker + re-fit button. Positioned
          in the upper-left over the map; z-10 keeps them above Cesium's
          canvas. */}
      <div className="absolute top-2 left-2 z-10 flex items-center gap-2">
        <BasemapPicker value={basemapId} onChange={setBasemapId} />
        <button
          type="button"
          onClick={fitToActivity}
          className="text-[11px] px-2 py-1 bg-navy-900/85 hover:bg-navy-800 border border-navy-700 text-white rounded backdrop-blur"
          title="Fit view to detection activity"
        >
          Fit to activity
        </button>
      </div>

      {/* Legend overlay — mirrors the intensity → color gradient. */}
      <div className="absolute bottom-2 right-2 z-10 flex items-center gap-1.5 text-[10px] text-white/90 bg-navy-900/80 border border-navy-700 rounded px-2 py-1 backdrop-blur">
        <span className="text-gray-400">Less</span>
        <span
          className="inline-block w-3 h-3 rounded-sm"
          style={{ backgroundColor: 'rgba(255, 181, 71, 0.75)' }}
        />
        <span
          className="inline-block w-3 h-3 rounded-sm"
          style={{ backgroundColor: 'rgba(255, 159, 67, 0.85)' }}
        />
        <span
          className="inline-block w-3 h-3 rounded-sm"
          style={{ backgroundColor: 'rgba(255, 59, 92, 0.9)' }}
        />
        <span className="text-gray-400">More</span>
      </div>
    </div>
  );
}
